import { api } from 'api/client';
import { API_ENDPOINTS } from 'config/api.config';
import {
  Application,
  ApplicationCreateRequest,
  ApplicationUpdateRequest,
  ApplicationAccessRequest,
  ApplicationCopyRequest,
  AppUser,
  AppUserFilter,
  AppUserStats,
  ApplicationLookup
} from 'types/application.types';
import { ListResponse, SuccessResponse } from 'types/api.types';

const buildQueryParams = (filter?: AppUserFilter): string => {
  if (!filter) return '';
  
  const params = new URLSearchParams();
  
  if (filter.limit !== undefined) params.append('limit', String(filter.limit));
  if (filter.offset !== undefined) params.append('offset', String(filter.offset));
  if (filter.has_registration !== undefined) params.append('has_registration', String(filter.has_registration));
  if (filter.has_deposit !== undefined) params.append('has_deposit', String(filter.has_deposit));
  if (filter.is_active !== undefined) params.append('is_active', String(filter.is_active));
  if (filter.locale) params.append('locale', filter.locale);
  if (filter.country) params.append('country', filter.country);
  if (filter.os) params.append('os', filter.os);
  if (filter.offer) params.append('offer', filter.offer);
  if (filter.search) params.append('search', filter.search);
  if (filter.date_from) params.append('date_from', filter.date_from);
  if (filter.date_to) params.append('date_to', filter.date_to);
  
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

export const applicationsApi = {
  list: async (
    limit: number = 20,
    offset: number = 0,
    search?: string,
  ): Promise<ListResponse<Application>> => {
    const response = await api.get<ListResponse<Application>>(
      API_ENDPOINTS.applications.list,
      { limit, offset, ...(search ? { search } : {}) }
    );
    return response.data;
  },

  get: async (id: number): Promise<Application> => {
    const response = await api.get<Application>(
      API_ENDPOINTS.applications.get(id)
    );
    return response.data;
  },

  create: async (data: ApplicationCreateRequest): Promise<SuccessResponse & { application: Application }> => {
    const response = await api.post<SuccessResponse & { application: Application }>(
      API_ENDPOINTS.applications.create,
      data
    );
    return response.data;
  },

  lookup: async (): Promise<{ applications: ApplicationLookup[] }> => {
    const response = await api.get<{ applications: ApplicationLookup[] }>(
      `${API_ENDPOINTS.applications.list}/lookup`
    );
    return response.data;
  },

  update: async (id: number, data: ApplicationUpdateRequest): Promise<SuccessResponse & { application: Application }> => {
    const response = await api.put<SuccessResponse & { application: Application }>(
      API_ENDPOINTS.applications.update(id),
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<SuccessResponse> => {
    const response = await api.delete<SuccessResponse>(
      API_ENDPOINTS.applications.delete(id)
    );
    return response.data;
  },

  listUsers: async (appId: number, filter?: AppUserFilter): Promise<ListResponse<AppUser>> => {
    const queryParams = buildQueryParams(filter);
    const response = await api.get<ListResponse<AppUser>>(
      `${API_ENDPOINTS.applications.listUsers(appId)}${queryParams}`
    );
    return response.data;
  },

  copy: async (id: number, data: ApplicationCopyRequest): Promise<SuccessResponse & { application: Application }> => {
    const response = await api.post<SuccessResponse & { application: Application }>(
      `${API_ENDPOINTS.applications.get(id)}/copy`,
      data
    );
    return response.data;
  },

  getActiveUsers: async (appId: number, limit: number = 50, offset: number = 0): Promise<ListResponse<AppUser>> => {
    const response = await api.get<ListResponse<AppUser>>(
      API_ENDPOINTS.applications.getActiveUsers(appId),
      { params: { limit, offset } }
    );
    return response.data;
  },

  getDeletedUsers: async (appId: number, limit: number = 50, offset: number = 0): Promise<ListResponse<AppUser>> => {
    const response = await api.get<ListResponse<AppUser>>(
      API_ENDPOINTS.applications.getDeletedUsers(appId),
      { params: { limit, offset } }
    );
    return response.data;
  },

  getUserStats: async (appId: number): Promise<AppUserStats> => {
    const response = await api.get<AppUserStats>(
      API_ENDPOINTS.applications.getUserStats(appId)
    );
    return response.data;
  },

  getUser: async (appId: number, userId: number): Promise<AppUser> => {
    const response = await api.get<AppUser>(
      API_ENDPOINTS.applications.getUser(appId, userId)
    );
    return response.data;
  },

  deleteUser: async (appId: number, userId: number, reason?: string): Promise<SuccessResponse> => {
    const response = await api.delete<SuccessResponse>(
      API_ENDPOINTS.applications.deleteUser(appId, userId),
      { data: reason ? { reason } : undefined }
    );
    return response.data;
  },

  restoreUser: async (appId: number, userId: number): Promise<SuccessResponse> => {
    const response = await api.put<SuccessResponse>(
      API_ENDPOINTS.applications.restoreUser(appId, userId)
    );
    return response.data;
  },

  exportUsers: async (appId: number, filter?: AppUserFilter): Promise<Blob> => {
    const queryParams = buildQueryParams(filter);
    const response = await api.get<Blob>(
      `${API_ENDPOINTS.applications.exportUsers(appId)}${queryParams}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  grantAccess: async (data: ApplicationAccessRequest): Promise<SuccessResponse> => {
    const response = await api.post<SuccessResponse>(
      API_ENDPOINTS.applications.grantAccess,
      data
    );
    return response.data;
  },

  revokeAccess: async (data: ApplicationAccessRequest): Promise<SuccessResponse> => {
    const response = await api.post<SuccessResponse>(
      API_ENDPOINTS.applications.revokeAccess,
      data
    );
    return response.data;
  },
};